/* eslint-disable import/no-cycle */

import React, { Fragment, createContext, useMemo } from 'react';
import PropTypes from 'prop-types';

import { Dialog, Transition } from '@headlessui/react';

import { checkChildren, useIsMobile } from '@hooks';
import { mergeClasses } from '@utils';

import DrawerClose from './Drawer.Close';
import DrawerContent from './Drawer.Content';
import DrawerTitle from './Drawer.Title';

export const DrawerContext = createContext();

/**
 * Drawer component that serves as a container for its child components:
 * `Drawer.Close`, `Drawer.Content`, `Drawer.Title`.
 *
 * @component Drawer
 * @param {Object} props - The properties for the Drawer component.
 * @param {React.ReactNode} props.children - The child components of the Drawer.
 * @param {string} [props.className] - Optional additional CSS classes to apply to the Drawer container.
 * @param {boolean} props.isOpen - Boolean indicating whether the Drawer is open.
 * @param {Function} props.setIsOpen - Function to set the state of the Drawer.
 * @param {Function} [props.onClose] - Optional function to be called when the Drawer is closed.
 * @param {Object} [restProps] - Additional properties to be passed to the Drawer container.
 * @returns {ReactElement} The rendered Drawer component.
 * @example
 * <Drawer
 *   isOpen={isOpen}
 *   setIsOpen={setIsOpen}
 *   onClose={handleClose}
 * >
 *   <Drawer.Close />
 *   <Drawer.Title>Title Here</Drawer.Title>
 *   <Drawer.Content>Content Here</Drawer.Content>
 * </Drawer>
 */
export default function Drawer({
  children,
  className,
  isOpen,
  setIsOpen,
  onClose,
  ...restProps
}) {
  checkChildren(children, [
    Dialog.Description,
    Drawer.Close,
    Drawer.Content,
    Drawer.Title
  ]);

  const { isMobile } = useIsMobile();

  const contextValue = useMemo(
    () => ({
      isOpen,
      setIsOpen
    }),
    [isOpen, setIsOpen]
  );

  const enterFrom = isMobile
    ? 'transform-gpu translate-y-8 opacity-0'
    : 'transform-gpu translate-x-8 opacity-0';
  const leaveTo = isMobile
    ? 'transform-gpu translate-y-8  opacity-0'
    : 'transform-gpu translate-x-8 opacity-0';
  return (
    <DrawerContext.Provider value={contextValue}>
      <Transition show={isOpen} as={Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 z-[6000]"
          onClose={() => {
            setIsOpen(false);
          }}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-in-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in-out duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray/30 backdrop-blur-sm" />
          </Transition.Child>

          <Transition.Child
            as={Fragment}
            enter="ease-in-out duration-200"
            enterFrom={enterFrom}
            enterTo="transform-gpu translate-x-0 opacity-100"
            leave="ease-in-out duration-200"
            leaveFrom="transform-gpu translate-x-0 opacity-100"
            leaveTo={leaveTo}
          >
            <div
              className={mergeClasses(
                'fixed inset-x-0 bottom-0 h-[90%] overflow-x-hidden overflow-y-scroll rounded-t-xl bg-white sm:inset-x-auto sm:inset-y-0 sm:right-0 sm:max-h-none sm:w-96 sm:rounded-none md:h-[100%]',
                className
              )}
              {...restProps}
            >
              <Dialog.Panel
                className={mergeClasses(
                  'box-border h-full w-full space-y-4 p-4',
                  className
                )}
                {...restProps}
              >
                {children}
              </Dialog.Panel>
            </div>
          </Transition.Child>
        </Dialog>
      </Transition>
    </DrawerContext.Provider>
  );
}

Drawer.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  isOpen: PropTypes.bool.isRequired,
  setIsOpen: PropTypes.func.isRequired,
  onClose: PropTypes.func
};

Drawer.defaultProps = {
  className: '',
  onClose: () => {}
};

Drawer.Close = DrawerClose;
Drawer.Content = DrawerContent;
Drawer.Title = DrawerTitle;
